import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../lib/translations';
import Section from '../components/UI/Section';
import Button from '../components/UI/Button';
import { ArrowLeft, Check } from 'lucide-react';
import { servicesCache } from '../lib/cache';

const ServiceDetail = () => {
  const { slug } = useParams();
  const { lang, setLang } = useLanguage();
  const t = translations[lang];
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(!servicesCache.data);
  const isFirstRender = useRef(true);

  const isTherapyPage = slug === 'therapy' || slug === 'terapi';
  const isCoachingPage = slug === 'coaching';
  const isCftPage = slug === 'compassion-focused-therapy' || slug === 'compassion-fokuseret-terapi';
  const isLivingAbroadPage = slug === 'living-abroad' || slug === 'udlandsophold';
  const isSupervisionPage = slug === 'supervision';

  useEffect(() => {
    const fetchService = async () => {
      // If we don't have cached data, show loading state
      if (!servicesCache.data) {
        setLoading(true);
      }
      
      try {
        const allServices = await servicesCache.get();
        
        const found = allServices.find(s => {
          if (!s.slug) return false;
          return (
            s.slug[lang] === slug || 
            s.slug.da === slug || 
            s.slug.en === slug ||
            s.id === slug // Fallback to ID if slug doesn't match
          );
        });

        if (found) {
          setService(found);
          
          if (isFirstRender.current) {
            isFirstRender.current = false;
            // On initial load, force the language state to match the slug used,
            // but ONLY if the English and Danish slugs are different.
            // If they are identical (e.g. 'coaching', 'supervision'), preserve the current language.
            if (found.slug.en !== found.slug.da) {
              if (found.slug.en === slug && lang !== 'en') {
                setLang('en');
              } else if (found.slug.da === slug && lang !== 'da') {
                setLang('da');
              }
            }
          } else {
            // On subsequent renders (e.g. user toggled language in header),
            // redirect the URL slug to match the new language
            if (found.slug.en !== found.slug.da) {
              if (found.slug.en === slug && lang === 'da') {
                navigate(`/services/${found.slug.da}`, { replace: true });
              } else if (found.slug.da === slug && lang === 'en') {
                navigate(`/services/${found.slug.en}`, { replace: true });
              }
            }
          }
        } else {
          console.error("Service not found for slug:", slug, "Available slugs:", allServices.map(s => s.slug));
          throw new Error('Service not found');
        }
      } catch (error) {
        console.error("Fetch error:", error);
        if (!servicesCache.data) {
          navigate('/services');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchService();
  }, [slug, lang, navigate, setLang]);

  if (loading) return <div className="pt-40 text-center font-serif text-stone-400">{t.common.loading}</div>;
  if (!service) return null;

  return (
    <div className="pt-20">
      <Section spacing="pb-12 md:pb-16 lg:pb-32">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-stone-400 hover:text-stone-900 mb-16 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          {t.common.back}
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-20">
          <div className="lg:col-span-2">
            <h1 className="text-4xl md:text-6xl font-serif text-stone-900 mb-12 leading-tight">
              {lang === 'en' && slug === 'supervision' ? 'Supervision' : 
               lang === 'en' && slug === 'therapy' ? 'Therapy' : 
               isLivingAbroadPage ? 'Living Abroad' : 
               isCoachingPage ? 'Coaching' :
               isCftPage ? (lang === 'da' ? 'Compassionfokuseret Terapi (CFT)' : `${service.title[lang]} (CFT)`) :
               service.title[lang]}
            </h1>
            
            {lang === 'en' && slug === 'supervision' ? (
              <div className="prose prose-stone prose-lg max-w-none text-stone-600 leading-relaxed space-y-6">
                <p>I offer clinical supervision using the framework of Compassion Focused Therapy (CFT).</p>
                <p>I am an approved supervisor from the Danish Psychological Association and the Danish Psychiatric Society.</p>
                <p>I offer supervision for psychologists and doctors who are either new to or looking to build and enhance skills in Compassion Focused Therapy.</p>
              </div>
            ) : lang === 'en' && slug === 'therapy' ? (
              <div className="space-y-8">
                <div className="prose prose-stone prose-lg max-w-none text-stone-600 leading-relaxed space-y-6">
                  <p>My priority is to provide a warm, respectful, safe and confidential space for our work. In our first sessions, our focus will be on getting to know you and the difficulties you have been experiencing. It is important to me that you feel heard and understood as we begin to make sense of the difficulties together and clarify your aims for our work. From there we discuss what direction the therapy might take to support you.</p>
                  <p>My way of working is rooted in Compassion Focused Therapy (CFT), an evidence-based scientific approach that integrates insights from Cognitive-behavioral therapy (CBT), attachment theory, neuroscience and evolutionary psychology.</p>
                </div>
                <div className="pt-8 border-t border-stone-100">
                  <Button to="/services/compassion-focused-therapy" variant="primary">
                    READ MORE ABOUT CFT
                  </Button>
                </div>
              </div>
            ) : isLivingAbroadPage ? (
              <div className="prose prose-stone prose-lg max-w-none text-stone-600 leading-relaxed space-y-6">
                <p>Moving abroad can be an exciting transition. It can bring with it new career or study opportunities, new friendships and the prospect of enhanced quality of life. It can also be challenging. Beyond the practical stresses of relocating, moving abroad can involve orienting to new and unspoken social norms, a new language and new friendship groups and work culture. Navigating all this can understandably be frustrating and anxiety provoking. There can be a feeling of loss of belonging, connection and self-confidence.</p>
                <p>Faced with these challenges it can be easy to get stuck. It might be overworking or over-adjusting at the expense of your own intuition and needs or you might be struggling with self-criticism and perfectionism.</p>
                <p>My priority is to provide a safe space for us to explore the challenges that you are facing and to provide tailored support to rebuild your self-confidence.</p>
                <p>Following my own move to Denmark from the UK in my late 20’s, I have experienced firsthand many of the challenges people face when relocating. Combined with clinical insights from supporting Internationals over the years, I am well positioned to help you to manage the practical and emotional demands of settling into life and work in Denmark.</p>
                <p>My experience is that Compassion Focused Therapy (CFT) is especially helpful in supporting Internationals to navigate the challenges of building a life and career abroad.</p>
                
                <div className="flex flex-wrap gap-4 pt-8 border-t border-stone-100">
                  <Button to="/om-mig" variant="outline" className="border-taupe-300 text-taupe-700 hover:border-taupe-500 hover:text-taupe-900">
                    MY FULL BACKGROUND
                  </Button>
                  <Button to="/services/compassion-focused-therapy" variant="primary">
                    READ MORE ABOUT CFT
                  </Button>
                </div>
              </div>
            ) : isCoachingPage ? (
              <div className="prose prose-stone prose-lg max-w-none text-stone-600 leading-relaxed space-y-6">
                {lang === 'da' ? (
                  <>
                    <p className="font-medium text-stone-700">Coaching kan være det rette for dig, hvis du:</p>
                    <ul className="space-y-2 pl-4 list-none">
                      <li className="flex items-start">
                        <span className="w-1.5 h-1.5 rounded-full bg-taupe-500 shrink-0 mt-2.5 mr-3" aria-hidden="true" />
                        <span>Står foran et nyt kapitel i dit arbejds- eller privatliv</span>
                      </li>
                      <li className="flex items-start">
                        <span className="w-1.5 h-1.5 rounded-full bg-taupe-500 shrink-0 mt-2.5 mr-3" aria-hidden="true" />
                        <span>Har et ønske om et rum til at udforske og få indsigt i egne mønstre; det kan være selvtvivl, selvkritik, præstationspres eller Imposter Syndrome.</span>
                      </li>
                      <li className="flex items-start">
                        <span className="w-1.5 h-1.5 rounded-full bg-taupe-500 shrink-0 mt-2.5 mr-3" aria-hidden="true" />
                        <span>Du søger sparring omkring dine værdier og retning, professionelt eller privat</span>
                      </li>
                      <li className="flex items-start">
                        <span className="w-1.5 h-1.5 rounded-full bg-taupe-500 shrink-0 mt-2.5 mr-3" aria-hidden="true" />
                        <span>Ønsker et rum til at reflektere over din ledelse, trivsel og resultater</span>
                      </li>
                    </ul>
                    <p className="pt-2">Min tilgang til coaching kombinerer den psykologiske videnskab bag Compassionfokuseret Terapi med principper fra kognitiv adfærdsterapi og positiv psykologi. Vi har fokus på at skabe mere klarhed over de udfordringer, du står overfor her og nu, udvikling af nye perspektiver og støtte til at tage konkrete skridt i den retning, du gerne vil.</p>
                  </>
                ) : (
                  <>
                    <p className="font-medium text-stone-700">Coaching can be right for you, if you are:</p>
                    <ul className="space-y-2 pl-4 list-none">
                      <li className="flex items-start">
                        <span className="w-1.5 h-1.5 rounded-full bg-taupe-500 shrink-0 mt-2.5 mr-3" aria-hidden="true" />
                        <span>Navigating a personal or professional transition, including international relocation</span>
                      </li>
                      <li className="flex items-start">
                        <span className="w-1.5 h-1.5 rounded-full bg-taupe-500 shrink-0 mt-2.5 mr-3" aria-hidden="true" />
                        <span>Wanting to explore your own patterns of self-doubt, self-criticism or performance anxiety</span>
                      </li>
                      <li className="flex items-start">
                        <span className="w-1.5 h-1.5 rounded-full bg-taupe-500 shrink-0 mt-2.5 mr-3" aria-hidden="true" />
                        <span>Looking to re-align with your core values and sense of direction</span>
                      </li>
                      <li className="flex items-start">
                        <span className="w-1.5 h-1.5 rounded-full bg-taupe-500 shrink-0 mt-2.5 mr-3" aria-hidden="true" />
                        <span>Wanting to enhance your leadership and performance</span>
                      </li>
                    </ul>
                    <p className="pt-2">My approach to Coaching combines the psychological science behind Compassion Focused Therapy (CFT) with Positive Psychology and cognitive behavioural principles to help you find more clarity around the obstacles you are facing, develop new perspectives, and to support you in taking tangible steps towards meaningful change and growth.</p>
                    <p>Coaching is primarily present and future-focused and goal-oriented.</p>
                  </>
                )}
              </div>
            ) : isCftPage ? (
              <div className="prose prose-stone prose-lg max-w-none text-stone-600 leading-relaxed space-y-8">
                <div>
                  {lang === 'en' ? (
                    <>
                      <p>CFT is an evidence-based scientific approach that integrates insights from attachment theory, neuroscience and evolutionary psychology.</p>
                      <p>This approach can help to explain why it is so easy to become our own worst enemy when life gets hard and we are struggling. Humans have tricky brains that are wired to protect us, even in ways that leave us feeling worse, whether that might be blaming ourselves or constantly struggling to prove ourselves.</p>
                      <p>The good news is that CFT can help to reconnect with and develop our human potential for caring for and supporting ourselves when things are difficult. CFT works by helping you to rebalance your emotional regulation systems, reducing stress and supporting you to feel more calm, connected and confident.</p>
                    </>
                  ) : (
                    <>
                      <p>CFT er en evidensbaseret psykologisk tilgang udviklet af professor Paul Gilbert. CFT integrerer viden fra tilknytningsteori, neurovidenskab og evolutionsteori.</p>
                      <p>Tilgangen kan hjælpe til at forklare, hvorfor det er så let at blive sin egen værste fjende, når livet er svært. Vores menneskehjerne er fantastisk, men den kan også være ret drilsk. Når vi møder udfordringer, så tændes der helt automatisk for nogle af de mere ”gamle” dele af hjernen som er præprogrammeret til at beskytte os som det absolut vigtigste. De ”nyere” dele af hjernen kan prøve at ”løse problemet” ved at tænke os ud af ubehaget, men der kan opstå uheldige tankeloops, hvor vi holdes fast i alarmberedskab og selvkritik.</p>
                      <p>Den gode nyhed er, at CFT kan hjælpe os til med at finde tilbage til et potentiale vi har, for at drage omsorg for os selv, når livet er svært. Gennem CFT får du hjælp til at opnå bedre følelsesmæssig balance og dæmpe stress. Du får støtte til at mærke mere ro, fællesskab med andre og tro på dig selv.</p>
                    </>
                  )}
                </div>

                <div className="border-t border-stone-100 pt-8">
                  <p className="font-medium text-stone-900 mb-6">
                    {lang === 'en' 
                      ? 'Because everyone is different, CFT blends evidence-based tools from different therapies to support you, including:'
                      : 'Fordi alle mennesker er forskellige, kombinerer CFT veldokumenterede metoder, herunder:'}
                  </p>
                  <ul className="space-y-5 list-none pl-0">
                    <li className="flex items-start gap-4">
                      <div className="w-6 h-6 rounded-full bg-taupe-50 flex items-center justify-center text-taupe-500 shrink-0 mt-1">
                        <Check className="w-4 h-4" />
                      </div>
                      <div>
                        <strong className="text-stone-900 block mb-0.5">
                          {lang === 'en' ? 'Mindful awareness' : 'Mindful opmærksomhed'}
                        </strong>
                        <span>
                          {lang === 'en' ? 'To become aware of our patterns and emotions.' : 'Til at øge bevidsthed om reaktionsmønstre og følelser'}
                        </span>
                      </div>
                    </li>
                    <li className="flex items-start gap-4">
                      <div className="w-6 h-6 rounded-full bg-taupe-50 flex items-center justify-center text-taupe-500 shrink-0 mt-1">
                        <Check className="w-4 h-4" />
                      </div>
                      <div>
                        <strong className="text-stone-900 block mb-0.5">
                          {lang === 'en' ? 'Training Compassion skills' : 'Træning af færdigheder i selvomsorg'}
                        </strong>
                        <span>
                          {lang === 'en' 
                            ? 'To shift our brain states into compassionate states that support clarity, strength and wisdom in tackling difficulties. This includes using imagery and breathing to shift our physiology.'
                            : 'som styrker evnen til at bringe hjernens tilstand over i en compassion tilstand præget af klarhed, visdom og styrke. Her anvendes bl.a. visualisering og åndedrætsøvelser til at tænde kroppens indre ro-system'}
                        </span>
                      </div>
                    </li>
                    <li className="flex items-start gap-4">
                      <div className="w-6 h-6 rounded-full bg-taupe-50 flex items-center justify-center text-taupe-500 shrink-0 mt-1">
                        <Check className="w-4 h-4" />
                      </div>
                      <div>
                        <strong className="text-stone-900 block mb-0.5">
                          {lang === 'en' ? 'Cognitive Behavioral Therapy' : 'Kognitiv adfærdsterapi'}
                        </strong>
                        <span>
                          {lang === 'en' 
                            ? 'To gently challenge looping negative thoughts and work with trying out new behaviours.'
                            : 'til nænsomt at udfordre uhjælpsomme tankemønstre og at afprøve nye vaner og adfærd'}
                        </span>
                      </div>
                    </li>
                    <li className="flex items-start gap-4">
                      <div className="w-6 h-6 rounded-full bg-taupe-50 flex items-center justify-center text-taupe-500 shrink-0 mt-1">
                        <Check className="w-4 h-4" />
                      </div>
                      <div>
                        <strong className="text-stone-900 block mb-0.5">
                          {lang === 'en' ? 'Psychodynamic therapy' : 'Psykodynamisk terapi'}
                        </strong>
                        <span>
                          {lang === 'en' 
                            ? 'To make sense of your ways of relating to yourself in the context of your life history.'
                            : 'til at skabe mening i hvordan du møder dig selv ud fra dine livserfaringer og tidlige relationer'}
                        </span>
                      </div>
                    </li>
                  </ul>
                </div>

                <div className="pt-0">
                  <h3 className="text-2xl font-serif text-stone-900 mb-3">
                    {lang === 'en' ? 'My background in CFT' : 'Min CFT baggrund'}
                  </h3>
                  <p className="mb-6">
                    {lang === 'en' 
                      ? 'I have trained with Professor Paul Gilbert OBE, the founder of Compassionate Focused Therapy and other leading world experts in the field. Most recently in 2026 I completed a year-long (British Psychological Society Approved) Post Graduate Diploma in CFT from the Compassionate Mind Foundation, UK.'
                      : 'Jeg har videreuddannet mig inden for retningen Compassionfokuseret Terapi hos nogle af de førende eksperter på området. I 2026 gennemførte jeg en 1-årig intensiv postgraduate diplomuddannelse i CFT fra Compassionate Mind Foundation, UK.'}
                  </p>
                  <Button to="/om-mig" variant="outline" className="border-taupe-300 text-taupe-700 hover:border-taupe-500 hover:text-taupe-900">
                    {lang === 'en' ? 'READ MY FULL BACKGROUND' : 'LÆS MERE OM MIN BAGGRUND'}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-8">
                <div 
                  className="prose prose-stone prose-lg max-w-none text-stone-600 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: service.description[lang] }}
                />
                {isTherapyPage && (
                  <div className="pt-8 border-t border-stone-100">
                    <Button to="/services/compassion-fokuseret-terapi" variant="primary">
                      LÆS MERE OM CFT
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="space-y-12 lg:pt-3">
            {!isTherapyPage && !isCoachingPage && !isLivingAbroadPage && !isSupervisionPage && (
              <div className={`${isCftPage ? 'bg-taupe-500' : 'bg-stone-900'} text-white p-12 rounded-3xl shadow-sm`}>
                {isCftPage ? (
                  <div className="space-y-8">
                    <div>
                      <h3 className="text-2xl font-serif mb-4 text-white">
                        {lang === 'en' ? 'Who can benefit from CFT?' : 'Hvem kan have glæde af CFT?'}
                      </h3>
                      <p className="text-white/90 text-sm mb-4 leading-relaxed">
                        {lang === 'en' 
                          ? 'My experience is that CFT is especially helpful in supporting Internationals to navigate the challenges of building a life and career abroad.'
                          : 'Der er evidens for CFT i forhold til en række forskellige udfordringer, herunder:'}
                      </p>
                      {lang === 'en' && (
                        <p className="text-xs font-semibold uppercase tracking-wider text-white/80 mb-4">
                          CFT has been shown to be effective for a range of difficulties, including:
                        </p>
                      )}
                      <ul className="space-y-3 list-none pl-0">
                        {(lang === 'en' ? [
                          "Persistent self-criticism or shame",
                          "Anxiety",
                          "Stress and depression",
                          "Low self-esteem, feelings of inadequacy",
                          "Perfectionism",
                          "Relationship difficulties & losses",
                          "Trauma and emotional wounds",
                          "Coping with chronic physical health conditions or illness"
                        ] : [
                          "Vedvarende selvkritik og skam",
                          "Angst",
                          "Stress og depression",
                          "Lavt selvværd, utilstrækkelighedsfølelse",
                          "Belastende perfektionisme",
                          "Problemer i relationer til andre, tab",
                          "Traume og følelsesmæssige svigt",
                          "At håndtere kronisk fysisk sygdom"
                        ]).map((item, idx) => (
                          <li key={idx} className="flex items-start gap-3">
                            <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-white shrink-0 mt-0.5">
                              <Check className="w-3 h-3" />
                            </div>
                            <span className="text-white text-sm">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ) : (
                  <>
                    <h3 className="text-2xl font-serif mb-6 text-white">
                      {lang === 'da' ? 'Klar til at starte?' : 'Get in touch'}
                    </h3>
                    <p className="text-stone-400 mb-10 leading-relaxed">
                      {lang === 'da' 
                        ? 'Kontakt mig for en uforpligtende samtale om, hvordan jeg kan hjælpe dig.'
                        : 'Contact me to discuss your needs'}
                    </p>
                    <Button to="/kontakt" variant="primary" className="w-full">
                      {lang === 'da' ? t.hero.cta_contact : 'CONTACT ME'}
                    </Button>
                  </>
                )}
              </div>
            )}
            
            <div className="aspect-video lg:aspect-square overflow-hidden rounded-3xl shadow-sm border border-stone-100 bg-white flex items-center justify-center">
              <img
                src={isCftPage 
                  ? (lang === 'en'
                      ? "https://assets.auxodev.dk/cdn-cgi/image/width=1000,quality=85,format=auto/assets/d2a473f1-f23b-48f1-982c-7e943d864f39/image_17.png"
                      : "https://assets.auxodev.dk/cdn-cgi/image/width=1000,quality=85,format=auto/assets/d2a473f1-f23b-48f1-982c-7e943d864f39/image_18.png"
                    )
                  : isCoachingPage
                    ? "https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=85,format=auto/assets/d2a473f1-f23b-48f1-982c-7e943d864f39/image_14.png"
                    : (service.image?.[0] || "https://assets.auxodev.dk/assets/d2a473f1-f23b-48f1-982c-7e943d864f39/16300ee2-dd1d-4de4-ab2a-19bf394f7d2e.jpg")
                }
                alt={isLivingAbroadPage ? 'Living Abroad' : isCoachingPage ? 'Coaching' : service.title[lang]}
                className={`w-full h-full ${isCftPage ? 'object-contain px-4 md:px-6' : 'object-cover'}`}
              />
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
};

export default ServiceDetail;
